// Monta a resposta e o encaminhamento a partir da mensagem aberta.
//
// Separado da tela porque é regra, não desenho: quem responde para quem, como
// o assunto é prefixado, e o que vai citado no corpo.

import type { Mensagem } from "../api/tipos";

/** Endereços da mensagem, para responder a todos sem incluir a si mesmo. */
function semDuplicatas(enderecos: (string | null | undefined)[], excluir: string[]): string[] {
  const fora = new Set(excluir.map((e) => e.toLowerCase()));
  const vistos = new Set<string>();
  const saida: string[] = [];
  for (const e of enderecos) {
    const limpo = (e ?? "").trim();
    if (!limpo) continue;
    const chave = limpo.toLowerCase();
    if (fora.has(chave) || vistos.has(chave)) continue;
    vistos.add(chave);
    saida.push(limpo);
  }
  return saida;
}

/**
 * Prefixa o assunto sem empilhar.
 *
 * "Re: Re: Re: proposta" é o que acontece quando cada resposta prefixa de novo.
 * Reconhece também "Res:" e "Enc:", que clientes em português produzem, e
 * "Fwd:", do inglês.
 */
export function prefixar(assunto: string | null | undefined, prefixo: "Re" | "Enc"): string {
  const base = (assunto ?? "").trim();
  const jaTem = new RegExp(`^(re|res|enc|fw|fwd)\\s*:\\s*`, "i");
  const limpo = base.replace(jaTem, "").trim();
  if (!limpo) return `${prefixo}: (sem assunto)`;
  return `${prefixo}: ${limpo}`;
}

/**
 * O corpo citado.
 *
 * Texto puro com ">" na frente, e não HTML: a área de escrita é textarea. A
 * atribuição vem antes, no formato que todo cliente de e-mail usa — é o que
 * permite a quem recebe entender de quando é o trecho citado.
 */
export function citar(m: Mensagem): string {
  const quando = m.data
    ? new Date(m.data).toLocaleString("pt-BR", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  const quem = m.remetente_nome ? `${m.remetente_nome} <${m.remetente}>` : m.remetente;

  // corpo_texto quando existe; senão o HTML já higienizado vira texto. Citar
  // HTML cru numa textarea mostraria as tags para quem responde.
  const original = m.corpo_texto?.trim() || textoDeHTML(m.corpo_html ?? "");

  const citado = original
    .split("\n")
    .map((linha) => "> " + linha)
    .join("\n");

  return `\n\nEm ${quando}, ${quem} escreveu:\n${citado}\n`;
}

/** Tira as tags para citar HTML numa área de texto. */
function textoDeHTML(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
}

export interface Preenchido {
  para: string;
  cc: string;
  assunto: string;
  corpo: string;
}

export function paraResponder(m: Mensagem, eu: string, aTodos: boolean): Preenchido {
  const meuEndereco = eu.trim().toLowerCase();
  // Mensagem que EU mandei — é o que se abre dentro de "Enviados".
  const souOAutor = (m.remetente ?? "").trim().toLowerCase() === meuEndereco;

  // DEFEITO REAL, achado usando: responder de dentro de "Enviados" mandava a
  // mensagem para MIM MESMO. O remetente daquela pasta sou eu, e responder
  // "para o remetente" fecha o laço na própria caixa — a mensagem sai,
  // é assinada, é entregue, e nunca chega em quem devia receber. Foi assim
  // que uma resposta ao Gmail voltou para jhou@jhou.tec.br em 15/09/2026.
  //
  // O certo, e o que Gmail, Outlook e Thunderbird fazem: responder algo que
  // eu escrevi endereça a QUEM EU ESCREVI. É continuar a conversa, não
  // começar uma comigo.
  const originais = semDuplicatas(m.destinatarios ?? [], []);

  // Reply-To manda quando existe: é o endereço que o remetente PEDIU que fosse
  // usado, e ignorá-lo faz a resposta de uma lista voltar só para quem postou.
  // Não se aplica quando o autor sou eu — meu próprio Reply-To me devolveria
  // a mensagem, que é exatamente o que este bloco existe para evitar.
  const paraOutros = m.cabecalhos?.["Reply-To"] || m.remetente || "";

  // Se a mensagem é minha e por algum motivo não tem destinatário registrado,
  // cair no remetente é melhor que abrir em branco: o contrato exige ao menos
  // um endereço, e sem nenhum o botão de enviar simplesmente falha.
  const destinos = souOAutor && originais.length > 0 ? originais : [paraOutros];

  const cc = aTodos
    ? semDuplicatas(
        [
          // Respondendo algo meu, os "outros" são os demais destinatários
          // originais; respondendo algo de terceiro, são os destinatários
          // menos eu. Nos dois casos `semDuplicatas` tira quem já está no Para.
          ...(souOAutor ? [] : (m.destinatarios ?? [])),
          ...(m.cabecalhos?.["Cc"] ?? "").split(","),
        ],
        [eu, ...destinos],
      )
    : [];

  return {
    para: destinos.join(", "),
    cc: cc.join(", "),
    assunto: prefixar(m.assunto, "Re"),
    corpo: citar(m),
  };
}

export function paraEncaminhar(m: Mensagem): Preenchido {
  return {
    para: "",
    cc: "",
    assunto: prefixar(m.assunto, "Enc"),
    corpo: citar(m),
  };
}
